import { useEffect, useState, useRef } from "react"
import { MinusIcon, PlusIcon } from "lucide-react"
import { Button, Group, Input, Label, NumberField } from "react-aria-components"
import { animate } from "motion/react"
import { motion } from "motion/react"

interface NumberPlusMinusProps {
  value: number
  onChange: (value: number) => void
  label?: string
  minValue?: number
  maxValue?: number
  step?: number
  className?: string
  decimals?: number
}

export function NumberPlusMinus({
  value,
  onChange,
  label,
  minValue = 0,
  maxValue,
  step = 0.01,
  className = "",
  decimals = 2,
}: NumberPlusMinusProps) {
  const [displayValue, setDisplayValue] = useState(value)
  const [isFocused, setIsFocused] = useState(false)
  const currentValueRef = useRef(value)

  useEffect(() => {
    if (!isFocused && Math.abs(currentValueRef.current - value) > 0.001) {
      const startValue = currentValueRef.current
      
      const controls = animate(startValue, value, {
        duration: 0.4,
        ease: "easeOut",
        onUpdate: (latest) => {
          setDisplayValue(latest)
          currentValueRef.current = latest
        },
      })

      return () => controls.stop()
    } else if (!isFocused) {
      setDisplayValue(value)
      currentValueRef.current = value
    }
  }, [value, isFocused])

  const formatValue = (val: number) => {
    return val.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  }

  return (
    <NumberField
      value={value}
      onChange={onChange}
      minValue={minValue}
      maxValue={maxValue}
      step={step}
      className={className}
    >
      {label && (
        <Label className="text-sm font-medium text-foreground mb-2 block">
          {label}
        </Label>
      )}
      <Group className="relative inline-flex h-9 w-full items-center overflow-hidden rounded-md border border-input text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none data-disabled:opacity-50 data-focus-within:border-ring data-focus-within:ring-[3px] data-focus-within:ring-ring/50 data-focus-within:has-aria-invalid:border-destructive data-focus-within:has-aria-invalid:ring-destructive/20 dark:data-focus-within:has-aria-invalid:ring-destructive/40">
        <Button
          slot="decrement"
          className="-ms-px flex aspect-square h-[inherit] items-center justify-center rounded-s-md border border-input bg-background text-sm text-muted-foreground/80 transition-[color,box-shadow] hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <MinusIcon size={16} aria-hidden="true" />
        </Button>
        <div className="relative w-full grow">
          <Input
            className="w-full grow bg-background px-3 py-2 text-center text-foreground tabular-nums"
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false)
              setDisplayValue(value)
              currentValueRef.current = value
            }}
          />
          {!isFocused && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center pointer-events-none bg-background"
              key={formatValue(displayValue)}
              initial={{ scale: 1.05, opacity: 0.8 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <span className="text-foreground tabular-nums">
                {formatValue(displayValue)}
              </span>
            </motion.div>
          )}
        </div>
        <Button
          slot="increment"
          className="-me-px flex aspect-square h-[inherit] items-center justify-center rounded-e-md border border-input bg-background text-sm text-muted-foreground/80 transition-[color,box-shadow] hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PlusIcon size={16} aria-hidden="true" />
        </Button>
      </Group>
    </NumberField>
  )
}
